import { css } from "@emotion/css";
import type { ButtonMenuItem, PrimaryPageAction } from "@octopusdeploy/design-system-components";
import { Badge, Tooltip, Switch } from "@octopusdeploy/design-system-components";
import { ExperimentIcon } from "@octopusdeploy/design-system-icons";
import { TextWithLabel as Text } from "@octopusdeploy/design-system-octopus-components";
import { themeTokens } from "@octopusdeploy/design-system-tokens";
import { useInlineStatusMutation } from "@octopusdeploy/octopus-react-client";
import type { EnvironmentResource, FeatureToggleResource, TenantResource, FeatureToggleEnvironmentResource, CreateFeatureToggleCommand, ProjectResource, Repository } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import * as React from "react";
import { useState } from "react";
import EnvironmentOrMissingChip from "~/areas/projects/components/FeatureToggles/EnvironmentOrMissingChip";
import FeatureToggleTargetsButton from "~/areas/projects/components/FeatureToggles/FeatureToggleTargetsButton";
import { FeatureTogglesDataTableNameCell } from "~/areas/projects/components/FeatureToggles/FeatureTogglesDataTableNameCell";
import { PreviewClientIdentifierDialog } from "~/areas/projects/components/FeatureToggles/PreviewClientIdentifierDialog";
import { RotateClientIdentifierDialog } from "~/areas/projects/components/FeatureToggles/RotateClientIdentifierDialog";
import type { ProjectContextProps } from "~/areas/projects/context/index";
import Dialog from "~/components/Dialog/Dialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import OkDialogLayout from "~/components/DialogLayout/OkDialogLayout";
import { GroupedDataTable } from "~/components/GroupedDataTable/GroupedDataTable";
import { useSpaceAwareNavigation } from "~/components/Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
import { PageContent } from "~/components/PageContent/PageContent";
export interface FeatureTogglesPageData {
    project: ProjectResource;
    featureToggles: FeatureToggleResource[];
    environments: EnvironmentResource[];
    tenants: TenantResource[];
}
export async function featureTogglesPageLoader(repository: Repository, projectContext: Promise<ProjectContextProps>): Promise<FeatureTogglesPageData> {
    const { state: { model: project }, } = await projectContext;
    const featureTogglesResponsePromise = repository.FeatureToggles.list(project);
    const environments = repository.Environments.all();
    const featureToggles = await featureTogglesResponsePromise;
    // Fetch all the tenants that are configured on toggles. Because we don't need to select tenants on this page, we can avoid tenants.all()
    const tenantIds = featureToggles.FeatureToggles.flatMap((f) => f.Environments)
        .flatMap((t) => t.TenantIds)
        .reduce((acc, tenantId) => acc.add(tenantId), new Set<string>());
    const excludedTenantIds = featureToggles.FeatureToggles.flatMap((f) => f.Environments)
        .flatMap((t) => t.ExcludedTenantIds)
        .reduce((acc, tenantId) => acc.add(tenantId), new Set<string>());
    const tenants = await repository.Tenants.all({ ids: [...tenantIds, ...excludedTenantIds] });
    const sortedFeatureToggles = featureToggles.FeatureToggles.sort((a, b) => (a.Name > b.Name ? 1 : -1));
    return {
        environments: await environments,
        tenants,
        featureToggles: sortedFeatureToggles,
        project,
    };
}
export interface FeatureTogglesPageProps {
    data: FeatureTogglesPageData;
}
export function FeatureTogglesPage({ data }: FeatureTogglesPageProps) {
    const { project, featureToggles, environments, tenants } = data;
    const { isOpen: isAddToggleDialogOpen, openDialog: openAddToggleDialog } = useDialogTrigger();
    const { isOpen: isPreviewDialogOpen, openDialog: openPreviewDialog } = useDialogTrigger();
    const { isOpen: isRotateDialogOpen, openDialog: openRotateDialog } = useDialogTrigger();
    const addToggleAction: PrimaryPageAction = {
        type: "button",
        label: "Add Feature Toggle",
        onClick: openAddToggleDialog,
    };
    const previewClientIdentifierAction: ButtonMenuItem = {
        type: "button",
        label: "Preview Client Identifiers",
        onClick: openPreviewDialog,
    };
    const rotateClientIdentifierAction: ButtonMenuItem = {
        type: "button",
        label: "Rotate Client Identifiers",
        onClick: openRotateDialog,
    };
    return (<PageContent header={{ title: "Feature Toggles", primaryAction: addToggleAction, chip: <AlphaBadge />, overflowActions: [previewClientIdentifierAction, rotateClientIdentifierAction] }}>
            <Dialog open={isAddToggleDialogOpen}>
                <AddFeatureToggleDialog project={project}/>
            </Dialog>
            <Dialog open={isPreviewDialogOpen}>
                <PreviewClientIdentifierDialog project={project} environments={environments}/>
            </Dialog>
            <Dialog open={isRotateDialogOpen}>
                <RotateClientIdentifierDialog project={project}/>
            </Dialog>
            <FeatureToggleTable featureToggles={featureToggles} tenants={tenants} environments={environments} project={project}/>
        </PageContent>);
}
interface FeatureToggleEnvironmentRow {
    FeatureToggle: FeatureToggleResource;
    Environment?: FeatureToggleEnvironmentResource;
    RowKey: string;
    IsChild: boolean;
}
interface FeatureToggleTableProps extends FeatureTogglesPageData {
}
function FeatureToggleTable({ featureToggles, tenants, environments, project }: FeatureToggleTableProps) {
    const environmentRow = (featureToggle: FeatureToggleResource, featureToggleEnvironment: FeatureToggleEnvironmentResource) => (<div>
            <EnvironmentOrMissingChip environmentId={featureToggleEnvironment.DeploymentEnvironmentId} environments={environments}/>
            {featureToggleEnvironment.MinimumVersion && (<>
                    <code>&gt;= {featureToggleEnvironment.MinimumVersion}</code>
                </>)}
            <FeatureToggleTargetsButton project={project} featureToggle={featureToggle} featureToggleEnvironment={featureToggleEnvironment} environments={environments} tenants={tenants}/>
        </div>);
    function buildToggleRows(featureToggles: FeatureToggleResource[]): FeatureToggleEnvironmentRow[] {
        const rows: FeatureToggleEnvironmentRow[] = [];
        for (const featureToggle of featureToggles) {
            const environments = featureToggle.Environments;
            let isChild = false;
            for (const toggle of environments) {
                rows.push({
                    FeatureToggle: featureToggle,
                    Environment: toggle,
                    RowKey: `${featureToggle.Id}${toggle.DeploymentEnvironmentId}`,
                    IsChild: isChild,
                });
                isChild = true;
            }
            // IF there are no environments defined, then push a row with just the feature toggle name
            if (environments.length === 0) {
                rows.push({
                    FeatureToggle: featureToggle,
                    RowKey: `${featureToggle.Id}`,
                    IsChild: false,
                });
            }
        }
        return rows;
    }
    return (<GroupedDataTable data={buildToggleRows(featureToggles)} columns={[
            {
                title: "Name",
                width: "25%",
                render: (data) => (data.FeatureToggle && !data.IsChild ? <FeatureTogglesDataTableNameCell featureToggle={data.FeatureToggle}/> : <></>),
                isChild: (data) => data.IsChild,
            },
            {
                title: "Status",
                width: "10%",
                render: (data) => (data.Environment ? <Switch value={data.Environment.IsEnabled} label={data.Environment.IsEnabled ? "On" : "Off"} disabled={true}/> : <div />),
            },
            {
                title: "Environment",
                render: (data) => (data.Environment ? environmentRow(data.FeatureToggle, data.Environment) : <div />),
            },
        ]} getRowKey={(data) => data.RowKey}/>);
}
interface AddFeatureToggleDialogProps {
    project: Readonly<ProjectResource>;
}
function AddFeatureToggleDialog({ project }: AddFeatureToggleDialogProps) {
    const { navigate } = useSpaceAwareNavigation();
    const [name, setName] = useState("");
    const { error: error, execute: add, isExecuting: busy, } = useInlineStatusMutation({
        name: "Add Feature Toggle",
        action: async (repository) => {
            const newFeatureToggle: CreateFeatureToggleCommand = { Name: name, ProjectId: project.Id, Environments: [] };
            return await repository.FeatureToggles.create(project, newFeatureToggle);
        },
        afterComplete: async (_, featureToggle) => {
            navigate(links.featureTogglePage.generateUrl({ spaceId: featureToggle.SpaceId, projectSlug: project.Slug, featureToggleSlug: featureToggle.Slug }));
        },
    });
    const errors = error ? [error] : undefined;
    return (<OkDialogLayout title="Add Feature Toggle" busy={busy} errors={errors} okButtonDisabled={!name} onOkClick={add}>
            <Text value={name} onChange={(value) => setName(value)} label="Name" autoFocus/>
        </OkDialogLayout>);
}
function AlphaBadge() {
    const styles = {
        badgeContainer: css({
            lineHeight: "0",
        }),
    };
    return (<div className={styles.badgeContainer}>
            <Tooltip content={"This feature is in development and may be unstable"}>
                <Badge variant={"feature"} size={"medium"} label={"Alpha"} icon={<ExperimentIcon size={20} color={themeTokens.color.badge.icon.feature}/>}/>
            </Tooltip>
        </div>);
}
